import React, { useRef, useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { Copy, Check, X, Table2, EyeOff, Eye, Download } from "lucide-react";
import { useSearchHighlight } from "../../../hooks/useSearchHighlight";
import { useTermSelection } from "../../../hooks/useTermSelection";
import { useDownloadPng } from "../../../hooks/useDownloadPng";
import { getMoleculeSVG, getMoleculeRender } from "../../../utils/Api";
import { sanitizeTableHtml } from "../../../utils/sanitizeTableHtml";

function highlightTermInText(
  text: string,
  query: string | undefined,
  isCurrent: boolean,
): React.ReactNode {
  if (!query) return text;
  const lower = text.toLowerCase();
  const lq = query.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let pos = 0;
  let key = 0;
  while (pos <= text.length) {
    const found = lower.indexOf(lq, pos);
    if (found === -1) {
      nodes.push(text.slice(pos));
      break;
    }
    if (found > pos) nodes.push(text.slice(pos, found));
    nodes.push(
      <mark
        key={key++}
        style={{
          background: isCurrent ? "#f97316" : "#fbbf24",
          color: "#111",
          borderRadius: "2px",
          padding: "0 1px",
        }}
      >
        {text.slice(found, found + lq.length)}
      </mark>,
    );
    pos = found + lq.length;
  }
  return nodes;
}

/**
 * A molecule can carry several distinct structures, which arrive as a
 * comma-separated list of SMILES (e.g. a Markush expansion). A valid SMILES
 * never contains a comma, so a comma is a reliable separator between structures.
 */
const splitSmilesList = (smiles: string | null | undefined): string[] => {
  if (!smiles) return [];
  return smiles
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Format coref text into a labelled metadata element. */
const formatMoleculeMetadata = (
  text: string | null | undefined,
): React.ReactNode | null => {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Numbers followed by % → Yield
  if (/\d+\.?\d*\s*%/.test(trimmed)) {
    return (
      <>
        <span className="text-slate-400 italic">Yield: </span>
        {trimmed}
      </>
    );
  }

  // A single letter, or numbers optionally followed by a single letter → Coreference
  if (/^\d+[a-zA-Z]?$/.test(trimmed) || /^[a-zA-Z]$/.test(trimmed)) {
    return (
      <>
        <span className="text-slate-400 italic">Coreference: </span>
        {trimmed}
      </>
    );
  }

  // Anything else → plain text
  return trimmed;
};

type BBox = [number, number, number, number];

type StructuredJSONDisplayProps = {
  rawData: any;
  readableData: any;
  imageHeight: number;
  imageWidth: number;
  searchQuery?: string;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number | undefined;
  selectedTerm?: string;
  onTermSelect?: (term: string) => void;
  hoveredParentUuid?: string | null;
  showDrawings?: boolean;
};

type ClusterUnit = {
  text: string;
  parentUuid: string; // originalTextUnit UUID for hover matching
};

type RenderedCluster = {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string; // Combined text of all units in cluster
  uuid: string; // Cluster UUID
  parentUuids: string[]; // Original text_unit UUIDs for hover mapping
  units: ClusterUnit[]; // Per-unit text + UUID for individual highlighting
  lineIndex: number; // Track which line this cluster belongs to
  lineUuid: string; // UUID of the parent textLine node (for text-tab click highlight)
  unitBboxes: { x: number; y: number; width: number; height: number }[]; // For debugging
  blockKind?: string; // From backend: "vertical_text" triggers rotated rendering
};

type RenderedMolecule = {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  uuid: string;
};

// Inline molecule image rendered directly inside a bounding box.
// Uses the DRKit render endpoint (graph) when available, falls back to CDK Depict (SMILES).
// Must be defined BEFORE StructuredJSONDisplay (const arrow functions are not hoisted).
const InlineMolSVG: React.FC<{
  smiles?: string;
  graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
  bboxWidth?: number | undefined;
  bboxHeight?: number | undefined;
}> = ({ smiles, graph, bboxWidth = 1, bboxHeight = 1 }) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState(false);

  useEffect(() => {
    // Don't fire until the container has been measured (displayProps still zero)
    if (bboxWidth === 0 || bboxHeight === 0) return;

    let cancelled = false;
    let objectUrl = "";
    setImageUrl("");
    setError(false);
    (async () => {
      try {
        let blob: Blob;
        if (graph?.symbols?.length) {
          // Preferred: render via DRKit backend
          const response = await getMoleculeRender({
            symbols: graph.symbols,
            coords: graph.coords,
            edges: graph.edges,
            bbox_width: bboxWidth,
            bbox_height: bboxHeight,
          });
          blob = response.data as Blob;
        } else if (smiles) {
          // Fallback: CDK Depict SMILES → SVG
          const response = await getMoleculeSVG(smiles);
          blob = new Blob([response.data], { type: "image/svg+xml" });
        } else {
          return;
        }
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrl = url;
        setImageUrl(url);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smiles, graph, bboxWidth, bboxHeight]);

  if (error) return null;
  if (!graph?.symbols?.length && !smiles) return null;
  if (!imageUrl)
    return (
      <div className="absolute inset-0 bg-white/60 animate-pulse pointer-events-none" />
    );

  return (
    <img
      src={imageUrl}
      alt="molecule"
      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
      style={{ background: "white" }}
    />
  );
};

const StructuredJSONDisplay: React.FC<StructuredJSONDisplayProps> = ({
  rawData,
  readableData,
  imageHeight,
  imageWidth,
  searchQuery,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  selectedTerm,
  onTermSelect,
  hoveredParentUuid,
  showDrawings = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [renderedClusters, setRenderedClusters] = useState<RenderedCluster[]>(
    [],
  );
  const [renderedMolecules, setRenderedMolecules] = useState<
    RenderedMolecule[]
  >([]);
  const [textScales, setTextScales] = useState<number[]>([]); // Per-cluster scales
  const [scalesCalculated, setScalesCalculated] = useState<boolean>(false);
  const [displayProps, setDisplayProps] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [selectedTable, setSelectedTable] = useState<{
    uuid: string;
    html: string;
  } | null>(null);
  const [disabledTables, setDisabledTables] = useState<Set<string>>(new Set());
  const [selectedMolecule, setSelectedMolecule] = useState<{
    smiles: string;
    graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
    bboxWidth?: number;
    bboxHeight?: number;
    coref?: string | null;
  } | null>(null);
  const { containerRef: moleculeContentRef, download: downloadMoleculePng } =
    useDownloadPng("molecule.png");

  // Build coref lookup: molecule uuid → coref text
  const corefMap = useMemo(() => {
    const map = new Map<string, string>();
    (rawData?.corefs || []).forEach((c: any) => {
      const molUuid =
        typeof c.molecule === "string" ? c.molecule : c.molecule?.uuid;
      if (molUuid && c.corefText) {
        map.set(molUuid, c.corefText);
      }
    });
    return map;
  }, [rawData]);

  // Metadata for consistent font sizing
  const metadata = rawData?.metadata || {};
  const standardHeight = metadata.standard_height || 0.02;
  const { handleDoubleClick, handleClick } = useTermSelection({ onTermSelect });

  const getTextFromItem = useMemo(
    () => (item: RenderedCluster | RenderedMolecule) => item.text,
    [],
  );

  // Extract all text_unit_preview objects for debugging
  const textUnitPreviewBoxes = useMemo(() => {
    if (!rawData?.objects) return [];
    return Object.values(rawData.objects)
      .filter((obj: any) => obj.kind === "text_unit_preview")
      .map((obj: any, idx: number) => ({
        bbox: obj.bbox,
        uuid: obj.uuid || `debug-unit-${idx}`,
      }));
  }, [rawData]);

  const searchItems = useMemo(
    () => [...renderedClusters, ...renderedMolecules],
    [renderedClusters, renderedMolecules],
  );

  const { itemRefs, getHighlightClass, currentHighlightIndex } = useSearchHighlight({
    searchQuery,
    items: searchItems,
    isCurrentSearchResult,
    currentOccurrenceIndex,
    getTextFromItem,
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && imageWidth > 0 && imageHeight > 0) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        if (containerWidth === 0 || containerHeight === 0) return;

        const scale = Math.min(
          containerWidth / imageWidth,
          containerHeight / imageHeight,
        );

        const scaledWidth = imageWidth * scale;
        const scaledHeight = imageHeight * scale;

        const offsetX = (containerWidth - scaledWidth) / 2;
        const offsetY = (containerHeight - scaledHeight) / 2;

        console.log("[StructuredJSONDisplay] dimensions:", {
          containerWidth,
          containerHeight,
          imageWidth,
          imageHeight,
          scaledWidth,
          scaledHeight,
        });

        setDisplayProps({
          x: offsetX,
          y: offsetY,
          width: scaledWidth,
          height: scaledHeight,
        });
      }
    };

    updateDimensions();

    // Use ResizeObserver for reliable detection (catches mount, zoom, and resize)
    let ro: ResizeObserver | null = null;
    if (containerRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateDimensions);
      ro.observe(containerRef.current);
    }
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
      ro?.disconnect();
    };
  }, [imageWidth, imageHeight]);

  // Process text lines and clusters to render clusters as single text pieces
  useEffect(() => {
    if (
      !rawData?.textLines ||
      displayProps.width === 0 ||
      displayProps.height === 0 ||
      imageHeight === 0 ||
      imageWidth === 0
    ) {
      setRenderedClusters([]);
      setRenderedMolecules([]);
      return;
    }

    const clusters: RenderedCluster[] = [];
    const molecules: RenderedMolecule[] = [];

    // Get global font size and spacing from metadata
    const metadata = rawData.metadata || {};
    const standardHeight = metadata.standard_height || 0.02;
    const standardSpacing = metadata.standard_spacing || standardHeight * 0.3;

    // Process each text line
    (rawData.textLines || []).forEach((line: any, lineIdx: number) => {
      let prevClusterRightEdge = 0;

      // Sort clusters left-to-right so the overlap-push logic below
      // (which uses prevClusterRightEdge) works correctly.
      const sortedClusters = (line.clusters || []).slice().sort((a: any, b: any) => {
        const ax =
          (a.textUnits || []).reduce(
            (min: number, u: any) => Math.min(min, u.bbox[0]),
            Infinity,
          ) || 0;
        const bx =
          (b.textUnits || []).reduce(
            (min: number, u: any) => Math.min(min, u.bbox[0]),
            Infinity,
          ) || 0;
        return ax - bx;
      });

      sortedClusters.forEach((cluster: any, clusterIdx: number) => {
        // Reading-order sort: top-to-bottom first, then left-to-right within
        // the same row. Pure x-sort breaks when a cluster contains multi-line
        // text (e.g. superscript "from the / Literature" next to a title) —
        // "Literature" sits below "from" at roughly the same x, so sorting
        // only by bbox[0] would give "from Literature the".
        const units = (cluster.textUnits || []).slice().sort((a: any, b: any) => {
          const ay = (a.bbox[1] + a.bbox[3]) / 2;
          const by = (b.bbox[1] + b.bbox[3]) / 2;
          const avgH = Math.max(
            a.bbox[3] - a.bbox[1],
            b.bbox[3] - b.bbox[1],
          );
          if (Math.abs(ay - by) > avgH * 0.5) {
            // Vertical text reads bottom-to-top: after rotate(-90deg) the
            // first unit in the string maps to the bottom of the strip, so
            // sort descending (bottommost unit first = first in reading order).
            return line.blockKind === "vertical_text" ? by - ay : ay - by;
          }
          return a.bbox[0] - b.bbox[0];
        });
        if (units.length === 0) return;

        // Combine text from all units in this cluster with spaces
        const combinedText = units.map((u: any) => u.text).join(" ");

        // Collect parent UUIDs for hover mapping
        const parentUuids = units
          .map((u: any) => u.originalTextUnit)
          .filter(Boolean);

        // Get Y bounds from all units (in case heights vary)
        let minY = Infinity,
          maxY = -Infinity;
        units.forEach((u: any) => {
          const [_x1, y1, _x2, y2] = u.bbox;
          minY = Math.min(minY, y1);
          maxY = Math.max(maxY, y2);
        });

        // For vertical text units are sorted top-to-bottom (not left-to-right),
        // so x-bounds must be derived from all units, not just first/last.
        const isVerticalCluster = line.blockKind === "vertical_text";
        let clusterStartX: number;
        let totalWidth: number;
        if (isVerticalCluster) {
          const minX = Math.min(...units.map((u: any) => u.bbox[0]));
          const maxX = Math.max(...units.map((u: any) => u.bbox[2]));
          clusterStartX = minX;
          totalWidth = maxX - minX;
        } else {
          const firstUnitBbox = units[0].bbox;
          const lastUnitBbox = units[units.length - 1].bbox;
          clusterStartX = firstUnitBbox[0];
          totalWidth = lastUnitBbox[2] - firstUnitBbox[0];
        }
        let currentXInPixels = clusterStartX * displayProps.width;
        const normalSpacePixels = standardSpacing * displayProps.width;

        // If this cluster overlaps with the previous one, move it (horizontal text only)
        if (!isVerticalCluster && clusterIdx > 0 && currentXInPixels < prevClusterRightEdge) {
          currentXInPixels = prevClusterRightEdge + normalSpacePixels;
          clusterStartX = currentXInPixels / displayProps.width;
        }

        const firstUnitBbox = units[0].bbox;

        const clusterHeight = maxY - minY;

        // Per-unit data for individual hover highlighting
        const clusterUnits: ClusterUnit[] = units.map((u: any) => ({
          text: u.text,
          parentUuid: u.originalTextUnit || "",
        }));

        const renderedCluster: RenderedCluster = {
          x: currentXInPixels,
          y: minY * displayProps.height,
          width: totalWidth * displayProps.width,
          height: clusterHeight * displayProps.height,
          text: combinedText,
          uuid: cluster.uuid,
          parentUuids,
          units: clusterUnits,
          lineIndex: lineIdx,
          lineUuid: line.uuid || "",
          unitBboxes: units.map((u: any) => ({
            x: (u.bbox[0] - firstUnitBbox[0]) * displayProps.width,
            y: (u.bbox[1] - minY) * displayProps.height,
            width: (u.bbox[2] - u.bbox[0]) * displayProps.width,
            height: (u.bbox[3] - u.bbox[1]) * displayProps.height,
          })),
          blockKind: line.blockKind,
        };

        clusters.push(renderedCluster);

        // Track right edge for next cluster
        prevClusterRightEdge = renderedCluster.x + renderedCluster.width;
      });
    });

    // Process molecules and rxns. Manually-added molecules have no bbox and
    // can't be positioned on the page image, so skip them here.
    (rawData.molecules || []).forEach((mol: any) => {
      if (!Array.isArray(mol.bbox) || mol.bbox.length < 4) return;
      const [x1, y1, x2, y2] = mol.bbox;
      const molData: any = {
        x: x1 * displayProps.width,
        y: y1 * displayProps.height,
        width: (x2 - x1) * displayProps.width,
        height: (y2 - y1) * displayProps.height,
        text: mol.text,
        uuid: mol.uuid,
        cls: mol.cls,
      };

      // Add rxn-specific data
      if (mol.cls === "rxn") {
        molData.reactants = (mol.reactants || []).map((r: any) => ({
          bbox: r.bbox,
          x: r.bbox[0] * displayProps.width,
          y: r.bbox[1] * displayProps.height,
          width: (r.bbox[2] - r.bbox[0]) * displayProps.width,
          height: (r.bbox[3] - r.bbox[1]) * displayProps.height,
          category: r.category,
        }));
        molData.products = (mol.products || []).map((p: any) => ({
          bbox: p.bbox,
          x: p.bbox[0] * displayProps.width,
          y: p.bbox[1] * displayProps.height,
          width: (p.bbox[2] - p.bbox[0]) * displayProps.width,
          height: (p.bbox[3] - p.bbox[1]) * displayProps.height,
          category: p.category,
        }));
        molData.conditions = (mol.conditions || []).map((c: any) => ({
          bbox: c.bbox,
          x: c.bbox[0] * displayProps.width,
          y: c.bbox[1] * displayProps.height,
          width: (c.bbox[2] - c.bbox[0]) * displayProps.width,
          height: (c.bbox[3] - c.bbox[1]) * displayProps.height,
          category: c.category,
        }));
      }

      molecules.push(molData);
    });

    setRenderedClusters(clusters);
    setRenderedMolecules(molecules);
    setScalesCalculated(false); // Reset to trigger recalculation
  }, [rawData, displayProps, imageHeight, imageWidth]);

  // Calculate scales with 60% primary bin and individual scaling for outliers
  useEffect(() => {
    if (
      !measureRef.current ||
      renderedClusters.length === 0 ||
      scalesCalculated
    )
      return;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const measureContainer = measureRef.current;
      if (!measureContainer) return;

      const spans = measureContainer.querySelectorAll("span[data-measure]");

      // First pass: calculate required scale for each cluster
      const requiredScales: number[] = [];
      spans.forEach((span, idx) => {
        const naturalWidth = span.scrollWidth;
        // Vertical text: the text runs along the cluster's LONG axis after
        // rotation, so compare the natural text width to the cluster height
        // (not width) to avoid squishing it into the narrow margin strip.
        const cluster = renderedClusters[idx];
        const isVertical = cluster?.blockKind === "vertical_text";
        const targetWidth = cluster
          ? isVertical
            ? cluster.height
            : cluster.width
          : naturalWidth;

        if (naturalWidth > 0 && targetWidth > 0) {
          requiredScales.push(targetWidth / naturalWidth);
        } else {
          requiredScales.push(1);
        }
      });

      // Sort required scales to find the scale that covers 60% of clusters
      const sortedScales = [...requiredScales].sort((a, b) => b - a); // Sort descending
      const targetIndex = Math.floor(sortedScales.length * 0.6); // 60% threshold
      const primaryBinScale = sortedScales[targetIndex] || 1;

      // Determine which clusters fall into the primary bin (within 5% tolerance)
      const tolerance = 0.05;
      const primaryBinMin = primaryBinScale * (1 - tolerance);
      const primaryBinMax = primaryBinScale * (1 + tolerance);

      // Hard rule: text must never exceed its bbox width. That means we
      // always compress down to `requiredScale` if needed (no floor).
      // The only cap is `min(..., 1.0)` so narrow text isn't stretched up.
      const scales: number[] = requiredScales.map((requiredScale) => {
        // Primary bin: use the shared primary bin scale, but never above
        // the cluster's own requiredScale — exceeding it would cause overflow.
        if (requiredScale >= primaryBinMin && requiredScale <= primaryBinMax) {
          return Math.min(primaryBinScale, requiredScale, 1.0);
        }

        // Outliers: their own required scale, capped at 1.0.
        return Math.min(requiredScale, 1.0);
      });

      setTextScales(scales);
      setScalesCalculated(true);
    });
  }, [renderedClusters, scalesCalculated]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative flex items-center justify-center overflow-hidden"
      onClick={handleClick}
    >
      {/* Hidden container for measuring text widths */}
      <div
        ref={measureRef}
        className="absolute opacity-0 pointer-events-none"
        style={{ left: -9999, top: -9999 }}
        aria-hidden="true"
      >
        {renderedClusters.map((item, idx) => {
          // Font size is proportional to the cluster's actual bbox height,
          // but floored at 50% of the body font size so genuinely small text
          // (e.g. citation lines) stays legible instead of collapsing to a dash.
          const minFontPx = standardHeight * displayProps.height * 0.5;
          const fontBase = item.blockKind === "vertical_text" ? item.width : item.height;
          const fontPx = Math.max(fontBase * 1.3, minFontPx);
          return (
            <span
              key={`measure-${idx}`}
              data-measure="true"
              style={{
                fontSize: `${fontPx}px`,
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {item.text}
            </span>
          );
        })}
      </div>

      {/* Inner page that matches original image aspect ratio */}
      <div
        className="relative bg-white border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.7)]"
        style={{
          width: `${displayProps.width}px`,
          height: `${displayProps.height}px`,
        }}
      >
        {/* Render text unit previews */}
        {renderedClusters.map((item, idx) => {
          const highlightClass = getHighlightClass(idx);
          const isHovered =
            hoveredParentUuid &&
            (item.parentUuids.includes(hoveredParentUuid) ||
              item.lineUuid === hoveredParentUuid);
          const isVertical = item.blockKind === "vertical_text";
          // Font size is based on the strip width for vertical text (that's the
          // x-height dimension), or cluster height for normal horizontal text.
          const minFontPx = standardHeight * displayProps.height * 0.5;
          const fontBase = isVertical ? item.width : item.height;
          const fontPx = Math.max(fontBase * 1.3, minFontPx);
          const linePx = Math.max(fontBase, minFontPx / 1.3);

          // Shared text content rendered inside whichever container applies.
          const textContent = (
            <span
              style={{
                display: "inline-block",
                transform: `scale(${textScales[idx] || 1})`,
                transformOrigin: "left center",
              }}
            >
              {item.units.map((unit, uIdx) => {
                const isUnitHovered =
                  isHovered && hoveredParentUuid === unit.parentUuid;
                return (
                  <span key={uIdx}>
                    {uIdx > 0 && " "}
                    <span
                      className={`transition-all duration-150 ${isUnitHovered ? "font-bold text-blue-700 underline decoration-blue-500 decoration-2 underline-offset-2" : ""}`}
                      style={isUnitHovered ? { fontSize: "115%" } : undefined}
                    >
                      {highlightTermInText(unit.text, searchQuery, idx === currentHighlightIndex)}
                    </span>
                  </span>
                );
              })}
            </span>
          );

          return (
            <div
              key={idx}
              ref={(el) => {
                itemRefs.current[idx] = el;
              }}
              // Outer div is always at the exact bbox position — same logic as
              // every other block. For vertical text the strip is narrow
              // (item.width) and tall (item.height); the rotation lives on the
              // inner div so the layout box matches the visual box.
              className={`absolute cursor-text select-text transition-all duration-150 ${highlightClass} ${
                isHovered ? "z-10" : "text-slate-800 z-0"
              }`}
              style={{
                left: `${item.x}px`,
                top: `${item.y}px`,
                width: `${item.width}px`,
                height: `${item.height}px`,
                overflow: "visible",
              }}
              title={item.text}
              onDoubleClick={() => handleDoubleClick(item.text)}
            >
              {isVertical ? (
                // Inner div: wide horizontal box that rotates to fill the strip.
                // translateY(item.height) + rotate(-90deg) maps its top-right
                // corner to (0,0) of the outer div, so visual and layout boxes
                // align.
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${item.height}px`,
                    height: `${item.width}px`,
                    transform: `translateY(${item.height}px) rotate(-90deg)`,
                    transformOrigin: "top left",
                    fontSize: `${fontPx}px`,
                    lineHeight: `${linePx}px`,
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {textContent}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    fontSize: `${fontPx}px`,
                    lineHeight: `${linePx}px`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {textContent}
                </div>
              )}
            </div>
          );
        })}

        {/* Debug: Every text_unit_preview object bounding box from JSON */}
        {textUnitPreviewBoxes.map((preview: any, idx: number) => (
          <div
            key={`preview-bbox-${idx}`}
            className="absolute pointer-events-none border border-blue-500 bg-blue-400/10"
            style={{
              left: `${preview.bbox[0] * displayProps.width}px`,
              top: `${preview.bbox[1] * displayProps.height}px`,
              width: `${(preview.bbox[2] - preview.bbox[0]) * displayProps.width}px`,
              height: `${(preview.bbox[3] - preview.bbox[1]) * displayProps.height}px`,
              zIndex: 30,
            }}
          />
        ))}
        {/* Render tables as hoverable bounding boxes */}
        {rawData?.molecules
          ?.filter(
            (item: any) => item.cls === "table" && item.data && item.data.html,
          )
          .map((item: any, idx: number) => {
            const [x1, y1, x2, y2] = item.bbox || [0, 0, 1, 1];
            const isDisabled = disabledTables.has(item.uuid);
            const isTableHovered = !!(
              hoveredParentUuid && item.uuid === hoveredParentUuid
            );

            return (
              <div
                key={`table-bbox-${item.uuid}`}
                className={`absolute transition-all duration-150 group ${
                  isDisabled
                    ? "pointer-events-none"
                    : "cursor-pointer hover:bg-yellow-400/30"
                } ${isTableHovered ? "bg-yellow-400/30" : ""}`}
                style={{
                  left: `${x1 * displayProps.width}px`,
                  top: `${y1 * displayProps.height}px`,
                  width: `${(x2 - x1) * displayProps.width}px`,
                  height: `${(y2 - y1) * displayProps.height}px`,
                  zIndex: 20,
                }}
                onClick={(e) => {
                  if (isDisabled) return;
                  e.stopPropagation();
                  setSelectedTable({ uuid: item.uuid, html: item.data.html });
                }}
                title={isDisabled ? "Table disabled" : "Click to view table"}
              >
                <div
                  className={`absolute top-1 right-1 flex items-center gap-1 transition-opacity ${
                    isDisabled
                      ? "opacity-70"
                      : "opacity-0 group-hover:opacity-100"
                  }`}
                  style={{ pointerEvents: "auto" }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDisabledTables((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.uuid)) {
                          next.delete(item.uuid);
                        } else {
                          next.add(item.uuid);
                        }
                        return next;
                      });
                    }}
                    className={`rounded p-1 shadow-md transition-colors ${
                      isDisabled
                        ? "bg-red-500 text-white hover:bg-red-600"
                        : "bg-slate-500 text-white hover:bg-slate-600"
                    }`}
                    title={isDisabled ? "Enable table" : "Disable table"}
                  >
                    {isDisabled ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  {!isDisabled && (
                    <div className="bg-blue-600 text-white rounded p-1 shadow-md">
                      <Table2 size={14} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        {/* Table detail modal — rendered via portal so it escapes any CSS transform parent */}
        {selectedTable &&
          ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={() => setSelectedTable(null)}
            >
              <div
                className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Table
                  </h3>
                  <button
                    onClick={() => setSelectedTable(null)}
                    className="p-1 hover:bg-slate-200 rounded transition-colors"
                  >
                    <X size={16} className="text-slate-500" />
                  </button>
                </div>
                {/* Table content */}
                <div className="flex-1 overflow-auto p-4">
                  <style>{`
                  .table-modal-container table { width: 100% !important; border-collapse: collapse !important; font-size: 13px !important; }
                  .table-modal-container th, .table-modal-container td { border: 1px solid #cbd5e1 !important; padding: 6px 10px !important; text-align: left; }
                  .table-modal-container th { background-color: #f1f5f9 !important; font-weight: 600; }
                  .table-modal-container td { background-color: #ffffff !important; }
                  .table-modal-container tr:hover td { background-color: #f8fafc !important; }
                `}</style>
                  <div
                    className="table-modal-container"
                    // LLM-generated HTML from a user-uploaded PDF — must be
                    // sanitised before injection (#584).
                    dangerouslySetInnerHTML={{
                      __html: sanitizeTableHtml(selectedTable.html),
                    }}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Molecule detail modal — rendered via portal */}
        {selectedMolecule &&
          (() => {
          const smilesParts = splitSmilesList(selectedMolecule.smiles);
          const isMultiSmiles = smilesParts.length > 1;
          return ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={() => setSelectedMolecule(null)}
            >
              <div
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Molecule Detail
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={downloadMoleculePng}
                      className="p-1 hover:bg-slate-200 rounded transition-colors"
                      title="Download as PNG"
                    >
                      <Download size={16} className="text-slate-500" />
                    </button>
                    <button
                      onClick={() => setSelectedMolecule(null)}
                      className="p-1 hover:bg-slate-200 rounded transition-colors"
                    >
                      <X size={16} className="text-slate-500" />
                    </button>
                  </div>
                </div>
                {/* Body */}
                <div
                  ref={moleculeContentRef}
                  className="p-6 flex flex-col items-center gap-4"
                >
                  {isMultiSmiles ? (
                    <div className="flex w-64 flex-col items-center justify-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-4 py-6 text-center">
                      <span className="text-sm font-semibold text-amber-800">
                        Cannot display drawing
                      </span>
                      <span className="text-xs text-amber-700">
                        This molecule consists of {smilesParts.length} structures
                        and cannot be drawn. All {smilesParts.length} SMILES are
                        listed below.
                      </span>
                    </div>
                  ) : selectedMolecule.graph?.symbols?.length ? (
                    <div className="relative w-64 h-48">
                      <InlineMolSVG
                        graph={selectedMolecule.graph}
                        bboxWidth={selectedMolecule.bboxWidth}
                        bboxHeight={selectedMolecule.bboxHeight}
                        smiles={selectedMolecule.smiles}
                      />
                    </div>
                  ) : (
                    <MoleculeModalImage smiles={selectedMolecule.smiles} />
                  )}
                  <div className="w-full space-y-2">
                    {selectedMolecule.smiles && (
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-semibold text-slate-500 w-20 shrink-0 pt-0.5">
                          SMILES
                        </span>
                        {isMultiSmiles ? (
                          <div className="flex flex-col gap-1 min-w-0">
                            {smilesParts.map((part, i) => (
                              <MoleculeModalSmilesCell key={i} smiles={part} />
                            ))}
                          </div>
                        ) : (
                          <MoleculeModalSmilesCell
                            smiles={selectedMolecule.smiles}
                          />
                        )}
                      </div>
                    )}
                    {selectedMolecule.coref &&
                      formatMoleculeMetadata(selectedMolecule.coref) && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-slate-500 w-20 shrink-0 pt-0.5">
                            Metadata
                          </span>
                          <span className="text-xs text-slate-700">
                            {formatMoleculeMetadata(selectedMolecule.coref)}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          );
          })()}

        {/* Render molecules separately (not tables or rxns) */}
        {showDrawings &&
          rawData?.molecules
          ?.filter((item: any) => item.cls === "molecule")
          .map((item: any, idx: number) => {
            const bbox = item.bbox;
            if (!bbox) return null;
            const [x1, y1, x2, y2] = bbox;
            const smiles = item.text || "";
            const graph = item.graph || null;
            const hasContent = graph?.symbols?.length || smiles;
            const coref = corefMap.get(item.uuid) ?? null;
            const smilesParts = splitSmilesList(smiles);
            const isMultiSmiles = smilesParts.length > 1;
            const isHovered = !!(
              hoveredParentUuid && item.uuid === hoveredParentUuid
            );

            return (
              <div
                key={`mol-standalone-${item.uuid || idx}`}
                className={`absolute cursor-pointer hover:bg-blue-400/30 transition-all duration-150 ${isHovered ? "bg-blue-400/30" : ""}`}
                style={{
                  left: `${x1 * displayProps.width}px`,
                  top: `${y1 * displayProps.height}px`,
                  width: `${(x2 - x1) * displayProps.width}px`,
                  height: `${(y2 - y1) * displayProps.height}px`,
                  zIndex: 20,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (smiles || graph)
                    setSelectedMolecule({
                      smiles,
                      graph,
                      bboxWidth: (x2 - x1) * displayProps.width,
                      bboxHeight: (y2 - y1) * displayProps.height,
                      coref,
                    });
                }}
                title={
                  isMultiSmiles
                    ? `This molecule consists of ${smilesParts.length} structures — click to view`
                    : smiles
                      ? "Click to view molecule"
                      : "Molecule"
                }
              >
                {isMultiSmiles ? (
                  <div className="flex h-full w-full items-center justify-center overflow-hidden bg-amber-50 px-1 text-center">
                    <span className="text-[10px] font-medium leading-tight text-amber-700">
                      {smilesParts.length} structures — cannot display drawing
                    </span>
                  </div>
                ) : (
                  hasContent && (
                    <InlineMolSVG
                      graph={graph}
                      smiles={smiles}
                      bboxWidth={(x2 - x1) * displayProps.width}
                      bboxHeight={(y2 - y1) * displayProps.height}
                    />
                  )
                )}
              </div>
            );
          })}

        {/* Render reaction chains with colored bounding boxes */}
        {showDrawings &&
          (rawData?.reactionChains || []).map((chain: any, chainIdx: number) => (
          <React.Fragment key={`chain-${chainIdx}`}>
            {/* Render bounding boxes for all components in the chain */}
            {(chain.allComponents || []).map((comp: any, compIdx: number) => {
              if (comp.type === "rxn") {
                // Don't render outer rxn box
                return null;
              }

              if (comp.type === "reactant" || comp.type === "product") {
                const bbox = comp.bbox;
                const compData = comp.data;
                if (!bbox) return null;

                // Check if this is a molecule (has smiles)
                const smiles = compData?.smiles || "";
                const isMolecule = smiles || compData?.category === "[Mol]";

                // Check if this molecule is being hovered on the original image
                const isHovered = !!(
                  hoveredParentUuid && compData?.uuid === hoveredParentUuid
                );

                return (
                  <ChainMoleculeBox
                    key={`chain-${chainIdx}-${comp.type}-${compIdx}`}
                    bbox={bbox}
                    smiles={smiles}
                    isMolecule={isMolecule}
                    isHovered={isHovered}
                    chainIdx={chainIdx}
                    chainColor={chain.color}
                    displayProps={displayProps}
                    compType={comp.type}
                    onMoleculeClick={
                      isMolecule && smiles
                        ? () =>
                            setSelectedMolecule({
                              smiles,
                              coref: corefMap.get(compData?.uuid) ?? null,
                            })
                        : undefined
                    }
                  />
                );
              }

              if (comp.type === "condition") {
                const condData = comp.data;

                // Render individual bboxes for each text unit in the condition (using preview positions)
                const textUnitBboxes = condData?.conditionTextUnitBboxes || [];

                if (textUnitBboxes.length === 0) {
                  // Fallback to overall condition bbox if no text unit bboxes
                  const bbox = comp.bbox;
                  if (!bbox) return null;
                  return (
                    <div
                      key={`chain-${chainIdx}-condition-${compIdx}`}
                      className="absolute"
                      style={{
                        left: `${bbox[0] * displayProps.width}px`,
                        top: `${bbox[1] * displayProps.height}px`,
                        width: `${(bbox[2] - bbox[0]) * displayProps.width}px`,
                        height: `${(bbox[3] - bbox[1]) * displayProps.height}px`,
                        zIndex: 5,
                      }}
                      title={`Condition (Chain ${chainIdx + 1})`}
                    />
                  );
                }

                return (
                  <React.Fragment
                    key={`chain-${chainIdx}-condition-${compIdx}`}
                  >
                    {textUnitBboxes.map(
                      (tu: { uuid: string; bbox: number[] }, tuIdx: number) => {
                        const bbox = tu.bbox;
                        if (!bbox || bbox.length < 4) return null;
                        const x1 = bbox[0]!;
                        const y1 = bbox[1]!;
                        const x2 = bbox[2]!;
                        const y2 = bbox[3]!;
                        return (
                          <div
                            key={`chain-${chainIdx}-condition-${compIdx}-tu-${tuIdx}`}
                            className="absolute"
                            style={{
                              left: `${x1 * displayProps.width}px`,
                              top: `${y1 * displayProps.height}px`,
                              width: `${(x2 - x1) * displayProps.width}px`,
                              height: `${(y2 - y1) * displayProps.height}px`,
                              zIndex: 5,
                            }}
                            title={`Condition (Chain ${chainIdx + 1})`}
                          />
                        );
                      },
                    )}
                  </React.Fragment>
                );
              }

              if (comp.type === "molecule") {
                const bbox = comp.bbox;
                // Find the molecule data from rawData.molecules
                const molData = (rawData?.molecules || []).find(
                  (m: any) => m.uuid === comp.uuid,
                );
                const smiles = molData?.text || "";
                if (!bbox) return null;

                // Check if this molecule is being hovered on the original image
                const isHovered = !!(
                  hoveredParentUuid && comp.uuid === hoveredParentUuid
                );

                return (
                  <ChainMoleculeBox
                    key={`chain-${chainIdx}-molecule-${compIdx}`}
                    bbox={bbox}
                    smiles={smiles}
                    isMolecule={true}
                    isHovered={isHovered}
                    chainIdx={chainIdx}
                    chainColor={chain.color}
                    displayProps={displayProps}
                    compType="molecule"
                    onMoleculeClick={
                      smiles
                        ? () =>
                            setSelectedMolecule({
                              smiles,
                              coref: corefMap.get(comp.uuid) ?? null,
                            })
                        : undefined
                    }
                  />
                );
              }

              if (comp.type === "coref") {
                const coref = comp.data;

                // Render individual bboxes for each text unit in the coref
                const textUnitBboxes = coref.corefTextUnitBboxes || [];

                if (textUnitBboxes.length === 0) return null;

                return (
                  <React.Fragment key={`chain-${chainIdx}-coref-${compIdx}`}>
                    {textUnitBboxes.map(
                      (tu: { uuid: string; bbox: number[] }, tuIdx: number) => {
                        const bbox = tu.bbox;
                        if (!bbox || bbox.length < 4) return null;
                        const x1 = bbox[0]!;
                        const y1 = bbox[1]!;
                        const x2 = bbox[2]!;
                        const y2 = bbox[3]!;
                        return (
                          <div
                            key={`chain-${chainIdx}-coref-${compIdx}-tu-${tuIdx}`}
                            className="absolute"
                            style={{
                              left: `${x1 * displayProps.width}px`,
                              top: `${y1 * displayProps.height}px`,
                              width: `${(x2 - x1) * displayProps.width}px`,
                              height: `${(y2 - y1) * displayProps.height}px`,
                              zIndex: 5,
                            }}
                            title={`Coref (Chain ${chainIdx + 1})`}
                          />
                        );
                      },
                    )}
                  </React.Fragment>
                );
              }

              return null;
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ─── Molecule modal helper components ────────────────────────────────────────

const MoleculeModalImage: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoke = "";
    if (smiles) {
      (async () => {
        try {
          const response = await getMoleculeSVG(smiles);
          const blob = new Blob([response.data], { type: "image/svg+xml" });
          const url = URL.createObjectURL(blob);
          revoke = url;
          setImageUrl(url);
        } catch {
          setError(true);
        }
      })();
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [smiles]);

  if (error || !smiles) {
    return (
      <div className="w-64 h-48 bg-slate-100 rounded flex items-center justify-center">
        <span className="text-[10px] text-slate-400 font-mono break-all text-center px-1">
          {smiles || "—"}
        </span>
      </div>
    );
  }

  if (!imageUrl) {
    return <div className="w-64 h-48 bg-slate-50 animate-pulse rounded" />;
  }

  return (
    <img src={imageUrl} alt="molecule" className="w-64 h-48 object-contain" />
  );
};

const MoleculeModalSmilesCell: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (smiles) {
      await navigator.clipboard.writeText(smiles);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const truncate = (s: string, max: number = 16) =>
    s.length > max ? s.substring(0, max) + "..." : s;

  return (
    <div className="flex items-center gap-1 min-w-0">
      <span
        className="text-xs font-mono text-slate-700 truncate min-w-0"
        title={smiles}
      >
        {truncate(smiles)}
      </span>
      {smiles && (
        <button
          onClick={handleCopy}
          className="p-0.5 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
          title="Copy SMILES"
        >
          {copied ? (
            <Check size={12} className="text-green-600" />
          ) : (
            <Copy size={12} className="text-slate-400" />
          )}
        </button>
      )}
    </div>
  );
};

// Molecule box component with hover popup for SMILES and copy option
const ChainMoleculeBox: React.FC<{
  bbox: number[];
  smiles: string;
  isMolecule: boolean;
  isHovered: boolean;
  chainIdx: number;
  chainColor: string;
  displayProps: { width: number; height: number };
  compType: string;
  onMoleculeClick?: (() => void) | undefined;
}> = ({
  bbox,
  smiles,
  isMolecule,
  isHovered,
  chainIdx,
  chainColor,
  displayProps,
  compType,
  onMoleculeClick,
}) => {
  const [localHovered, setLocalHovered] = useState(false);
  const [tooltipHovered, setTooltipHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  const truncateSmiles = (s: string, maxLen: number = 24): string => {
    if (!s) return "";
    return s.length > maxLen ? s.substring(0, maxLen) + "..." : s;
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (smiles) {
      await navigator.clipboard.writeText(smiles);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const showTooltip = (localHovered || tooltipHovered) && smiles;

  const x1 = bbox[0] ?? 0;
  const y1 = bbox[1] ?? 0;
  const x2 = bbox[2] ?? 0;
  const y2 = bbox[3] ?? 0;

  return (
    <div
      className="absolute transition-all duration-150"
      style={{
        left: `${x1 * displayProps.width}px`,
        top: `${y1 * displayProps.height}px`,
        width: `${(x2 - x1) * displayProps.width}px`,
        height: `${(y2 - y1) * displayProps.height}px`,
        backgroundColor:
          isHovered || localHovered ? "rgba(96, 165, 250, 0.3)" : "transparent",
        zIndex: isHovered || localHovered ? 20 : 5,
        ...(onMoleculeClick ? { cursor: "pointer" } : {}),
      }}
      onMouseEnter={() => setLocalHovered(true)}
      onMouseLeave={() => setLocalHovered(false)}
      onClick={(e) => {
        if (onMoleculeClick) {
          e.stopPropagation();
          onMoleculeClick();
        }
      }}
    >
      {isMolecule && smiles && <ChainMoleculeImage smiles={smiles} />}
      {showTooltip && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-30
                     bg-white border border-gray-300 rounded shadow-md
                     flex items-center gap-1 p-1
                     after:content-[''] after:absolute after:top-full after:left-0 after:w-full after:h-2"
          style={{ width: "200px" }}
          onMouseEnter={() => setTooltipHovered(true)}
          onMouseLeave={() => setTooltipHovered(false)}
        >
          <div
            className="flex-1 text-xs font-mono text-gray-700 truncate px-1"
            title={smiles}
          >
            {truncateSmiles(smiles)}
          </div>
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            title="Copy SMILES"
          >
            {copied ? (
              <Check size={14} className="text-green-600" />
            ) : (
              <Copy size={14} className="text-gray-500" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Simplified molecule image component for use inside chain boxes
const ChainMoleculeImage: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (smiles) {
      const fetchImage = async () => {
        try {
          const response = await getMoleculeSVG(smiles);
          const objectBlob = new Blob([response.data], {
            type: "image/svg+xml",
          });
          const objectURL = URL.createObjectURL(objectBlob);
          setImageUrl(objectURL);
        } catch (err) {
          console.error("Error fetching molecule image:", err);
          setError(true);
        }
      };
      fetchImage();

      return () => {
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }
      };
    }
  }, [smiles]);

  if (error || !imageUrl) {
    return null; // Don't show fallback, just the colored box
  }

  return (
    <img
      src={imageUrl}
      alt="Molecule"
      className="w-full h-full object-contain"
    />
  );
};

// Component to render molecule with SVG fetched from CDK Depict
const MoleculeImage: React.FC<{
  item: any;
  highlightClass: string;
  itemRef: (el: HTMLDivElement | null) => void;
  onDoubleClick: () => void;
  isHovered?: boolean;
}> = ({
  item,
  highlightClass,
  itemRef,
  onDoubleClick,
  isHovered: isParentHovered = false,
}) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [hovered, setHovered] = useState<boolean>(false);
  const [tooltipHovered, setTooltipHovered] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // Truncate SMILES for display in fallback box
  const truncateSmiles = (smiles: string, maxLen: number = 24): string => {
    if (!smiles) return "";
    return smiles.length > maxLen
      ? smiles.substring(0, maxLen) + "..."
      : smiles;
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.text) {
      await navigator.clipboard.writeText(item.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const showTooltip = hovered || tooltipHovered;

  useEffect(() => {
    if (item.text) {
      setLoading(true);
      setError(false);
      const fetchImage = async () => {
        try {
          const response = await getMoleculeSVG(item.text);
          const objectBlob = new Blob([response.data], {
            type: "image/svg+xml",
          });
          const objectURL = URL.createObjectURL(objectBlob);
          setImageUrl(objectURL);
        } catch (err) {
          console.error("Error fetching molecule image:", err);
          setError(true);
        } finally {
          setLoading(false);
        }
      };
      fetchImage();

      return () => {
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }
      };
    }
  }, [item.text]);

  // Calculate scaled dimensions when hovered from parent
  const scale = isParentHovered ? 1.1 : 1;
  const scaledWidth = item.width * scale;
  const scaledHeight = item.height * scale;
  const offsetX = (scaledWidth - item.width) / 2;
  const offsetY = (scaledHeight - item.height) / 2;

  return (
    <div
      ref={itemRef}
      className={`absolute flex items-center justify-center cursor-pointer transition-all duration-150 ${highlightClass} ${
        isParentHovered ? "bg-red-400/30 z-10" : "z-0"
      }`}
      style={{
        left: `${item.x - offsetX}px`,
        top: `${item.y - offsetY}px`,
        width: `${scaledWidth}px`,
        height: `${scaledHeight}px`,
      }}
      title={item.text}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {loading && (
        <div className="w-full h-full bg-gray-100/50 animate-pulse rounded" />
      )}
      {error && !loading && (
        <div className="w-full h-full bg-blue-400/30 flex items-center justify-center overflow-hidden">
          <span className="text-xs font-mono text-blue-700 px-1 text-center break-all">
            {truncateSmiles(item.text)}
          </span>
        </div>
      )}
      {imageUrl && !loading && !error && (
        <img
          src={imageUrl}
          alt="Molecule"
          className="max-w-full max-h-full object-contain"
        />
      )}
      {showTooltip && item.text && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-20
                     bg-white border border-gray-300 rounded shadow-md
                     flex items-center gap-1 p-1
                     after:content-[''] after:absolute after:top-full after:left-0 after:w-full after:h-2"
          style={{ width: "200px" }}
          onMouseEnter={() => setTooltipHovered(true)}
          onMouseLeave={() => setTooltipHovered(false)}
        >
          <div
            className="flex-1 text-xs font-mono text-gray-700 truncate px-1"
            title={item.text}
          >
            {truncateSmiles(item.text)}
          </div>
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            title="Copy SMILES"
          >
            {copied ? (
              <Check size={14} className="text-green-600" />
            ) : (
              <Copy size={14} className="text-gray-500" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default StructuredJSONDisplay;
